import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { studioTransport, type UploadTransport } from './transport'
import { UploadManager } from './UploadManager'
import type { QuotaSnapshot, ResumableUpload, UploadItem } from './types'

/**
 * Envoltorio React sobre UploadManager.
 *
 * El núcleo (`upload/`) no importa React a propósito: es la lógica más
 * delicada del producto —troceo, concurrencia, reintentos, persistencia— y
 * mantenerla agnóstica es lo que hizo que el cambio de Vue a React no costara
 * nada aquí.
 */
export function useUploadQueue(projectId: string, transport?: UploadTransport) {
  const [items, setItems] = useState<Map<string, UploadItem>>(new Map())
  const [quota, setQuota] = useState<QuotaSnapshot | null>(null)
  const [batchError, setBatchError] = useState<string | null>(null)
  const [resumable, setResumable] = useState<ResumableUpload[]>([])

  const managerRef = useRef<UploadManager | null>(null)

  if (managerRef.current === null) {
    managerRef.current = new UploadManager(projectId, transport ?? studioTransport(projectId), {
      onItemChange: (item) => {
        setItems((previous) => new Map(previous).set(item.id, item))
      },
      onQuotaChange: setQuota,
      onBatchError: setBatchError,
    })
  }

  const refreshResumable = useCallback(async () => {
    const found = (await managerRef.current?.listResumable()) ?? []
    setResumable(found)
  }, [])

  useEffect(() => {
    void UploadManager.prune()
    // Al entrar en el proyecto se ofrecen las subidas que quedaron a medias.
    // Con enlaces peruanos y subidas de dos horas, esto no es un caso raro.
    void refreshResumable()

    const manager = managerRef.current

    return () => {
      // Al desmontar se cancelan las subidas activas: dejarlas huérfanas
      // consumiría cuota reservada que nadie liberaría hasta que expirase.
      manager?.dispose()
    }
  }, [refreshResumable])

  const enqueue = useCallback((files: File[]) => {
    setBatchError(null)
    void managerRef.current?.enqueue(files)
  }, [])

  const cancel = useCallback((itemId: string) => {
    managerRef.current?.cancel(itemId)
  }, [])

  const resume = useCallback(
    async (target: ResumableUpload, file: File) => {
      setBatchError(null)

      try {
        await managerRef.current?.resume(target, file)
        await refreshResumable()
      } catch (error) {
        setBatchError(error instanceof Error ? error.message : 'No se pudo reanudar.')
      }
    },
    [refreshResumable],
  )

  const list = useMemo(() => [...items.values()], [items])

  const summary = useMemo(() => {
    const totalBytes = list.reduce((sum, item) => sum + item.sizeBytes, 0)
    const sentBytes = list.reduce((sum, item) => sum + item.bytesSent, 0)
    const active = list.filter(
      (item) => item.status === 'uploading' || item.status === 'preparing',
    ).length

    return {
      totalBytes,
      sentBytes,
      active,
      done: list.filter((item) => item.status === 'done').length,
      failed: list.filter((item) => item.status === 'failed').length,
      percent: totalBytes === 0 ? 0 : Math.round((sentBytes / totalBytes) * 100),
    }
  }, [list])

  return { items: list, quota, batchError, resumable, summary, enqueue, cancel, resume }
}
